/**
 * MathEnginePage — orchestrator for the standalone Math Engine
 * workflow. After the Phase 97 refactor, every presentational piece
 * (resume panel, pickers, confirm panel, generation loader, results /
 * exit-review forms, stage result card, upsell) lives in
 * `components/math-engine/`. This file owns ONLY:
 *
 *   • the state machine (phase / stage / unit / mini / lesson chain)
 *   • the API choreography (start activation → diagnostic → lesson 1 →
 *     exit review → lesson N → wrap-up)
 *   • the page shell + which child component is mounted in which phase
 *
 * Nothing visual lives here other than the page chrome and the four
 * "builder card" entry tiles (Close the Gap / Custom Build / Extended
 * Rich Task / Single LFHC) — those sit on the unit-picker screen and
 * are short enough not to warrant their own file.
 */

import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  Calculator,
  ChevronRight,
  Compass,
  FileText,
  Loader2,
  Sparkles,
  Target,
} from "lucide-react";
import { toast } from "sonner";
import TrilliumIcon from "../components/TrilliumIcon";
import MathUnitOverview from "../components/MathUnitOverview";
import TaskBuilder from "../components/TaskBuilder";
import WorksheetBuilder from "../components/WorksheetBuilder";
import UnitTestGeneratorDialog from "../components/UnitTestGeneratorDialog";
import ConfirmPanel from "../components/math-engine/ConfirmPanel";
import GenerationLoader from "../components/math-engine/GenerationLoader";
import GradePicker from "../components/math-engine/GradePicker";
import MiniUnitCard from "../components/math-engine/MiniUnitCard";
import MiniUnitPairCard from "../components/math-engine/MiniUnitPairCard";
import SplitNoticeBanner from "../components/math-engine/SplitNoticeBanner";
import ResumePanel from "../components/math-engine/ResumePanel";
import StageResult from "../components/math-engine/StageResult";
import UnitCard from "../components/math-engine/UnitCard";
import UpsellScreen from "../components/math-engine/UpsellScreen";
import { track } from "../lib/analytics";
import {
  checkMathEngineAccess,
  deleteMathEngineWorkflow,
  listMathEngineInFlight,
  listMathEngineUnits,
  resumeMathEngineWorkflow,
  startMathDiagnostic,
  startMathEngine,
  startMathExitReview,
  startMathLesson1,
  startMathLesson2,
  startMathUnitSummary,
} from "../lib/api";

const MathEnginePage = () => {
  const [accessLoading, setAccessLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(false);
  const [grade, setGrade] = useState(null);
  const [units, setUnits] = useState([]);
  const [unitsLoading, setUnitsLoading] = useState(false);
  const [unit, setUnit] = useState(null);
  const [mini, setMini] = useState(null);
  // Generation state machine.
  //   phase: "idle" | "generating" | "ready"
  //   stage: "activation" | "diagnostic" | "lesson1" | "lesson2"
  //        | "exit_review" | "unit_summary"
  const [phase, setPhase] = useState("idle");
  const [stage, setStage] = useState("activation");
  // Worksheet builder side-panel — null = main menu; "close_the_gap" or
  // "custom_build" mounts the builder in its respective mode.
  const [worksheetMode, setWorksheetMode] = useState(null);
  // Task builder side-panel — null = main menu; "extended" or "lfhc"
  // mounts the builder in its respective mode (Phase 78).
  const [taskMode, setTaskMode] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activationActivity, setActivationActivity] = useState(null);
  // Kept separately so the results form can render alongside the
  // Lesson 1 result (and so re-rolling lesson 1 still has the
  // diagnostic's question metadata to map results onto).
  const [diagnosticActivity, setDiagnosticActivity] = useState(null);
  const [lesson1Activity, setLesson1Activity] = useState(null);
  // The most recent exit review (used as the branching anchor for the
  // next lesson generation).
  const [exitReviewActivity, setExitReviewActivity] = useState(null);
  // Full chain of subsequent lessons (Lesson 2, 3, 4, …) in order. The
  // most recent is the "current" one for chaining purposes.
  const [lesson2Chain, setLesson2Chain] = useState([]);
  const [lastResults, setLastResults] = useState(null);
  const [lastExitReview, setLastExitReview] = useState(null);
  const [submittingResults, setSubmittingResults] = useState(false);
  const [submittingExitReview, setSubmittingExitReview] = useState(false);
  const [wrappingUp, setWrappingUp] = useState(false);
  const [unitTestDialogOpen, setUnitTestDialogOpen] = useState(false);
  const [mode, setMode] = useState("activation_only");
  const [workflows, setWorkflows] = useState([]);
  // Feb 10 2026 — real-time backend progress. Set by each `startMath*`
  // call's `onProgress` callback, forwarded to `<GenerationLoader>`.
  // Reset at the start of every run so a fresh phase never inherits
  // the tail of the previous phase's waypoint.
  const [progress, setProgress] = useState("");

  useEffect(() => {
    checkMathEngineAccess()
      .then((data) => setHasAccess(!!data.has_access))
      .catch(() => setHasAccess(false))
      .finally(() => setAccessLoading(false));
    // Fire-and-forget — empty list when the user has no access.
    listMathEngineInFlight()
      .then(setWorkflows)
      .catch(() => setWorkflows([]));
  }, []);

  useEffect(() => {
    if (!grade) return;
    setUnit(null);
    setUnitsLoading(true);
    listMathEngineUnits(grade)
      .then(setUnits)
      .catch((e) => toast.error("Could not load units", { description: e?.message }))
      .finally(() => setUnitsLoading(false));
  }, [grade]);

  // Hide the "First N Days" warm-up unit from the unit picker — it's a
  // routines block, not a content unit, and won't be used to launch the
  // adaptive workflow. Excluded from the Math Engine by design, single
  // grade and pair alike.
  const contentUnits = useMemo(
    () => units.filter((u) => !/^First\s+\d+\s+Days/i.test(u.name)),
    [units]
  );

  const runGeneration = async (chosenMode) => {
    if (!unit) return;
    // If the unit has mini-units but none is picked yet, do nothing — the
    // UI shouldn't expose the generation buttons in that state, but guard
    // anyway in case of a re-mount race.
    const hasMinis = (unit.mini_units || []).length > 0;
    if (hasMinis && !mini) return;
    setMode(chosenMode);
    setStage("activation");
    setPhase("generating");
    setProgress("");
    setActivity(null);
    setActivationActivity(null);
    setDiagnosticActivity(null);
    setLesson1Activity(null);
    setExitReviewActivity(null);
    setLesson2Chain([]);
    setLastResults(null);
    setLastExitReview(null);
    try {
      const result = await startMathEngine({
        grade: unit.grade,
        unit_number: unit.unit_number,
        mini_unit_label: mini?.label || null,
        mode: chosenMode,
      }, { onProgress: setProgress });
      setActivity(result);
      setActivationActivity(result);
      setPhase("ready");
      toast.success("Activation ready — saved to your Library.");
    } catch (err) {
      console.error(err);
      toast.error("Math Engine generation failed", {
        description: err?.message || "Please try again in a moment.",
      });
      setPhase("idle");
    }
  };

  const runDiagnostic = async () => {
    if (!unit) return;
    setStage("diagnostic");
    setPhase("generating");
    setProgress("");
    // Keep the activation visible state in memory so a "Re-roll" can
    // fall back to it. The on-screen artifact swaps to the diagnostic.
    setActivity(null);
    setDiagnosticActivity(null);
    try {
      const result = await startMathDiagnostic({
        grade: unit.grade,
        unit_number: unit.unit_number,
        mini_unit_label: mini?.label || null,
        after_activity_id: activationActivity?.id || null,
      }, { onProgress: setProgress });
      setActivity(result);
      setDiagnosticActivity(result);
      setPhase("ready");
      track("math_diagnostic_generated", {
        grade: unit.grade,
        unit_number: unit.unit_number,
        mini_unit_label: mini?.label || "",
      });
      toast.success("Diagnostic ready — saved to your Library.");
    } catch (err) {
      console.error(err);
      toast.error("Diagnostic generation failed", {
        description: err?.message || "Please try again in a moment.",
      });
      setPhase("ready"); // fall back to the activation result
      setStage("activation");
      setActivity(activationActivity);
    }
  };

  const runLesson1 = async (results) => {
    if (!unit || !diagnosticActivity) return;
    const payload = results || lastResults;
    if (!payload) return;
    setLastResults(payload);
    setSubmittingResults(true);
    setStage("lesson1");
    setPhase("generating");
    setProgress("");
    setActivity(null);
    setLesson1Activity(null);
    try {
      const result = await startMathLesson1({
        grade: unit.grade,
        unit_number: unit.unit_number,
        mini_unit_label: mini?.label || null,
        after_diagnostic_id: diagnosticActivity.id,
        results: payload,
      }, { onProgress: setProgress });
      setActivity(result);
      setLesson1Activity(result);
      setPhase("ready");
      toast.success("Lesson 1 ready — saved to your Library.");
    } catch (err) {
      console.error(err);
      toast.error("Lesson 1 generation failed", {
        description: err?.message || "Please try again in a moment.",
      });
      // Fall back to the diagnostic so the results form is still visible.
      setPhase("ready");
      setStage("diagnostic");
      setActivity(diagnosticActivity);
    } finally {
      setSubmittingResults(false);
    }
  };

  // Identifies the lesson currently being reviewed: the most-recent
  // entry in lesson2Chain, otherwise Lesson 1.
  const currentLessonForExitReview = () =>
    lesson2Chain && lesson2Chain.length > 0
      ? lesson2Chain[lesson2Chain.length - 1]
      : lesson1Activity;

  const runExitReview = async (buckets) => {
    if (!unit) return;
    const reviewingLesson = currentLessonForExitReview();
    if (!reviewingLesson) return;
    const payload = buckets || lastExitReview;
    if (!payload) return;
    setLastExitReview(payload);
    setSubmittingExitReview(true);
    setStage("exit_review");
    setPhase("generating");
    setProgress("");
    setActivity(null);
    setExitReviewActivity(null);
    try {
      const result = await startMathExitReview({
        grade: unit.grade,
        unit_number: unit.unit_number,
        mini_unit_label: mini?.label || null,
        after_lesson_id: reviewingLesson.id,
        mastered_pct: payload.mastered_pct,
        developing_pct: payload.developing_pct,
        reteach_pct: payload.reteach_pct,
        note: payload.note || "",
      }, { onProgress: setProgress });
      setActivity(result);
      setExitReviewActivity(result);
      setPhase("ready");
      toast.success("Tomorrow's call is in — saved to your Library.");
    } catch (err) {
      console.error(err);
      toast.error("Exit ticket review failed", {
        description: err?.message || "Please try again in a moment.",
      });
      // Fall back to whatever lesson is on screen.
      setPhase("ready");
      setStage(
        reviewingLesson.output_type === "math_lesson1" ? "lesson1" : "lesson2"
      );
      setActivity(reviewingLesson);
    } finally {
      setSubmittingExitReview(false);
    }
  };

  const runLessonNext = async () => {
    if (!unit || !exitReviewActivity) return;
    setStage("lesson2");
    setPhase("generating");
    setProgress("");
    setActivity(null);
    try {
      const result = await startMathLesson2({
        grade: unit.grade,
        unit_number: unit.unit_number,
        mini_unit_label: mini?.label || null,
        after_exit_review_id: exitReviewActivity.id,
      }, { onProgress: setProgress });
      setActivity(result);
      setLesson2Chain((prev) => [...prev, result]);
      setPhase("ready");
      const idx =
        ((result?.inputs || {}).math_engine || {}).lesson_index || "next";
      toast.success(`Lesson ${idx} ready — saved to your Library.`);
    } catch (err) {
      console.error(err);
      toast.error("Lesson generation failed", {
        description: err?.message || "Please try again in a moment.",
      });
      setPhase("ready");
      setStage("exit_review");
      setActivity(exitReviewActivity);
    }
  };

  const runUnitSummary = async () => {
    if (!unit) return;
    setWrappingUp(true);
    setStage("unit_summary");
    setPhase("generating");
    setProgress("");
    setActivity(null);
    try {
      const result = await startMathUnitSummary({
        grade: unit.grade,
        unit_number: unit.unit_number,
        mini_unit_label: mini?.label || null,
      }, { onProgress: setProgress });
      setActivity(result);
      setPhase("ready");
      toast.success("End-of-unit summary saved to your Library.");
    } catch (err) {
      console.error(err);
      toast.error("Could not write the unit summary", {
        description: err?.message || "Please try again in a moment.",
      });
      // Fall back to the previous result on screen.
      const fallback =
        exitReviewActivity ||
        (lesson2Chain.length ? lesson2Chain[lesson2Chain.length - 1] : null) ||
        lesson1Activity ||
        diagnosticActivity ||
        activationActivity;
      if (fallback) {
        const t = fallback.output_type;
        setStage(
          t === "math_lesson1"
            ? "lesson1"
            : t === "math_lesson2"
            ? "lesson2"
            : t === "math_diagnostic"
            ? "diagnostic"
            : t === "math_exit_review"
            ? "exit_review"
            : "activation"
        );
        setActivity(fallback);
        setPhase("ready");
      } else {
        setPhase("idle");
      }
    } finally {
      setWrappingUp(false);
    }
  };

  const reRollCurrent = () => {
    if (stage === "unit_summary") return runUnitSummary();
    if (stage === "lesson2") return runLessonNext();
    if (stage === "exit_review") return runExitReview();
    if (stage === "lesson1") return runLesson1();
    if (stage === "diagnostic") return runDiagnostic();
    return runGeneration(mode);
  };

  const resetToUnits = () => {
    setPhase("idle");
    setStage("activation");
    setActivity(null);
    setActivationActivity(null);
    setDiagnosticActivity(null);
    setLesson1Activity(null);
    setExitReviewActivity(null);
    setLesson2Chain([]);
    setLastResults(null);
    setLastExitReview(null);
    setUnit(null);
    setMini(null);
  };

  // Selecting a new unit clears any previously-chosen mini-unit so the
  // mini-unit picker shows correctly for the new unit.
  const handleSelectUnit = (u) => {
    setUnit(u);
    setMini(null);
  };

  // Resume an in-flight workflow from the panel. Fetches all artifacts
  // for the (grade, unit, mini) tuple, rehydrates the wizard state, and
  // lands on the most-recent stage so the teacher can continue.
  const handleResume = async (wf) => {
    try {
      // Load the parent unit (so all its metadata + mini-units are
      // available) and force the matching mini if any.
      let allUnits = [];
      try {
        allUnits = await listMathEngineUnits(wf.grade);
      } catch {
        allUnits = [];
      }
      const parent =
        allUnits.find((u) => u.unit_number === wf.unit_number) || {
          grade: wf.grade,
          unit_number: wf.unit_number,
          name: wf.unit_name,
          unit_id: `${wf.grade} — Unit ${wf.unit_number} — ${wf.unit_name}`,
          day_budget: "",
          focus: "",
          overall_expectations: [],
          specific_expectations: [],
          cross_strand: [],
          ai_notes: "",
          leap_note: "",
          mini_units: [],
        };
      let miniObj = null;
      if (wf.mini_unit_label) {
        miniObj =
          (parent.mini_units || []).find((m) => m.label === wf.mini_unit_label) || {
            label: wf.mini_unit_label,
            name: wf.mini_unit_name,
            specific_expectations: [],
          };
      }
      setUnit(parent);
      setMini(miniObj);

      const artifacts = await resumeMathEngineWorkflow({
        grade: wf.grade,
        unit_number: wf.unit_number,
        mini_unit_label: wf.mini_unit_label || null,
      });

      // Set the per-stage anchors so the wizard knows what to chain off.
      if (artifacts.math_activation) setActivationActivity(artifacts.math_activation);
      if (artifacts.math_diagnostic) setDiagnosticActivity(artifacts.math_diagnostic);
      if (artifacts.math_lesson1) setLesson1Activity(artifacts.math_lesson1);
      if (artifacts.math_exit_review) setExitReviewActivity(artifacts.math_exit_review);
      if (Array.isArray(artifacts.math_lesson2_chain)) {
        setLesson2Chain(artifacts.math_lesson2_chain);
      } else if (artifacts.math_lesson2) {
        setLesson2Chain([artifacts.math_lesson2]);
      } else {
        setLesson2Chain([]);
      }

      // Pick the most-recent stage and land there.
      const order = [
        ["math_unit_summary", "unit_summary"],
        ["math_lesson2", "lesson2"],
        ["math_exit_review", "exit_review"],
        ["math_lesson1", "lesson1"],
        ["math_diagnostic", "diagnostic"],
        ["math_activation", "activation"],
      ];
      let landed = null;
      for (const [otype, stageName] of order) {
        if (artifacts[otype]) {
          landed = { artifact: artifacts[otype], stage: stageName };
          break;
        }
      }
      if (!landed) {
        toast.info("Couldn't find the saved artifacts — start the unit again.");
        return;
      }
      setStage(landed.stage);
      setActivity(landed.artifact);
      setPhase("ready");
      const stageWord =
        landed.stage === "unit_summary"
          ? "unit summary"
          : landed.stage === "lesson2"
          ? `Lesson ${
              ((landed.artifact?.inputs || {}).math_engine || {}).lesson_index || "2"
            }`
          : landed.stage === "exit_review"
          ? "exit review"
          : landed.stage === "lesson1"
          ? "Lesson 1"
          : landed.stage === "diagnostic"
          ? "diagnostic"
          : "activation";
      toast.success(`Resumed at ${stageWord}.`);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } catch (err) {
      console.error(err);
      toast.error("Couldn't resume that workflow", {
        description: err?.message || "Open it from the Library instead.",
      });
    }
  };

  return (
    <main className="bg-[#f4f5f7] text-[#1c2d3d]">
      <section className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8 flex items-center gap-3">
          <span className="h-12 w-12 rounded-xl bg-[#96ded1]/30 grid place-items-center text-[#1c2d3d]">
            <Calculator className="h-6 w-6" />
          </span>
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/70 inline-flex items-center gap-1.5">
              <TrilliumIcon size={12} className="text-[#1c2d3d]" /> Math Engine
              <span className="text-[#4a6984]/40">·</span> SCDSB scope and sequence
            </p>
            <h1
              data-testid="math-engine-hero-heading"
              className="font-black text-3xl text-[#1c2d3d] tracking-tight"
            >
              {worksheetMode
                ? "Build a worksheet, the right way."
                : taskMode === "extended"
                ? "Build a rich task, the right way."
                : taskMode === "lfhc"
                ? "Build a question, the right way."
                : "Build a math unit, the right way."}
            </h1>
          </div>
        </div>

        {accessLoading ? (
          <div className="ae-card p-8 flex items-center justify-center gap-3 text-[#4a6984]">
            <Loader2 className="h-5 w-5 animate-spin" /> Checking access…
          </div>
        ) : !hasAccess ? (
          <UpsellScreen />
        ) : worksheetMode ? (
          <WorksheetBuilder mode={worksheetMode} onClose={() => setWorksheetMode(null)} />
        ) : taskMode ? (
          <TaskBuilder mode={taskMode} onClose={() => setTaskMode(null)} />
        ) : phase === "generating" && unit ? (
          <GenerationLoader unit={unit} mini={mini} stage={stage} mode={mode} progress={progress} />
        ) : phase === "ready" && activity && unit ? (
          <StageResult
            activity={activity}
            unit={unit}
            mini={mini}
            stage={stage}
            diagnosticActivity={diagnosticActivity}
            lesson1Activity={lesson1Activity}
            exitReviewActivity={exitReviewActivity}
            lesson2Chain={lesson2Chain}
            onAgain={reRollCurrent}
            onNext={stage === "activation" ? runDiagnostic : undefined}
            onSubmitResults={runLesson1}
            submittingResults={submittingResults}
            onSubmitExitReview={runExitReview}
            submittingExitReview={submittingExitReview}
            onLogNextExitTicket={runExitReview}
            onGenerateLessonNext={runLessonNext}
            onWrapUpUnit={runUnitSummary}
            wrappingUp={wrappingUp}
            onOpenUnitTest={() => setUnitTestDialogOpen(true)}
            onBack={resetToUnits}
          />
        ) : (
          <div className="space-y-6" data-testid="math-engine-workflow">
            <ResumePanel
              workflows={workflows}
              onResume={handleResume}
              onDelete={async (wf) => {
                try {
                  const res = await deleteMathEngineWorkflow({
                    grade: wf.grade,
                    unit_number: wf.unit_number,
                    mini_unit_label: wf.mini_unit_label || null,
                  });
                  setWorkflows((cur) =>
                    cur.filter(
                      (x) =>
                        !(
                          x.grade === wf.grade &&
                          x.unit_number === wf.unit_number &&
                          (x.mini_unit_label || "") === (wf.mini_unit_label || "")
                        )
                    )
                  );
                  toast.success(
                    `Removed ${wf.grade} · Unit ${wf.unit_number} (${res?.deleted || 0} lessons deleted).`
                  );
                } catch (err) {
                  toast.error(
                    err?.response?.data?.detail || "Could not delete this unit."
                  );
                }
              }}
            />

            {/* Phase 90 — active-unit overview with mid-quiz + wrap-up
                checkpoints. Self-hides for non-Pro+ users. */}
            <MathUnitOverview refreshKey={activity?.id || phase} />

            {/* Worksheet Builder + Task Builder entry — four cards before the unit picker */}
            <div
              data-testid="math-builder-entry"
              className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3"
            >
              <button
                type="button"
                onClick={() => setWorksheetMode("close_the_gap")}
                data-testid="worksheet-card-gap"
                className="ae-card p-5 text-left transition hover:border-[#96ded1] hover:shadow-md group"
              >
                <div className="flex items-start gap-3">
                  <span className="h-10 w-10 rounded-lg bg-[#96ded1]/30 grid place-items-center text-[#1c2d3d] shrink-0">
                    <Target className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#4a6984]/70 mb-1">
                      Worksheet Builder
                    </div>
                    <h3 className="font-extrabold text-lg text-[#1c2d3d] tracking-tight mb-1 group-hover:text-[#1c2d3d]">
                      Close the Gap
                    </h3>
                    <p className="text-sm text-[#4a6984]/85 leading-snug">
                      Revisit what you taught in your last lesson or two. We
                      pre-fill the form from your recent Math Engine activities.
                    </p>
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setWorksheetMode("custom_build")}
                data-testid="worksheet-card-custom"
                className="ae-card p-5 text-left transition hover:border-[#96ded1] hover:shadow-md group"
              >
                <div className="flex items-start gap-3">
                  <span className="h-10 w-10 rounded-lg bg-[#96ded1]/30 grid place-items-center text-[#1c2d3d] shrink-0">
                    <FileText className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#4a6984]/70 mb-1">
                      Worksheet Builder
                    </div>
                    <h3 className="font-extrabold text-lg text-[#1c2d3d] tracking-tight mb-1">
                      Custom Build
                    </h3>
                    <p className="text-sm text-[#4a6984]/85 leading-snug">
                      Build a worksheet for any grade, strand and expectation.
                      Generate all three difficulty levels at once for
                      differentiated stations.
                    </p>
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setTaskMode("extended")}
                data-testid="task-card-extended"
                className="ae-card p-5 text-left transition hover:border-[#96ded1] hover:shadow-md group"
              >
                <div className="flex items-start gap-3">
                  <span className="h-10 w-10 rounded-lg bg-[#96ded1]/30 grid place-items-center text-[#1c2d3d] shrink-0">
                    <Compass className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#4a6984]/70 mb-1">
                      Task Builder
                    </div>
                    <h3 className="font-extrabold text-lg text-[#1c2d3d] tracking-tight mb-1">
                      Extended Rich Task
                    </h3>
                    <p className="text-sm text-[#4a6984]/85 leading-snug">
                      A full classroom task — Parallel, Open, or Three-Part
                      Investigation. Student doc + teacher facilitation guide.
                    </p>
                  </div>
                </div>
              </button>
              <button
                type="button"
                onClick={() => setTaskMode("lfhc")}
                data-testid="task-card-lfhc"
                className="ae-card p-5 text-left transition hover:border-[#96ded1] hover:shadow-md group"
              >
                <div className="flex items-start gap-3">
                  <span className="h-10 w-10 rounded-lg bg-[#96ded1]/30 grid place-items-center text-[#1c2d3d] shrink-0">
                    <Sparkles className="h-5 w-5" />
                  </span>
                  <div>
                    <div className="text-[10px] font-black uppercase tracking-[0.18em] text-[#4a6984]/70 mb-1">
                      Task Builder
                    </div>
                    <h3 className="font-extrabold text-lg text-[#1c2d3d] tracking-tight mb-1">
                      Low-Floor, High-Ceiling Question
                    </h3>
                    <p className="text-sm text-[#4a6984]/85 leading-snug">
                      One strong question with floor, ceiling, sample answers,
                      and a stuck-student prompt. Or a bank of 3 / 5.
                    </p>
                  </div>
                </div>
              </button>
            </div>

            {/* Step 1 */}
            <div>
              <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/70 mb-2">
                Step 1 · Pick the grade you teach
              </p>
              <div
                data-testid="math-engine-split-picker-note"
                className="mb-3 rounded-xl border-2 border-[#4a6984]/30 bg-[#4a6984]/10 px-4 py-3"
              >
                <p className="text-sm text-[#1c2d3d] leading-relaxed max-w-2xl">
                  Split-grade buttons open a planning view showing how each unit
                  divides between your two grades. Lesson generation is
                  single-grade for now.
                </p>
              </div>
              <GradePicker selected={grade} onSelect={setGrade} />
            </div>

            {/* Step 2 */}
            {grade && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/70 mb-2 flex items-center gap-2">
                  Step 2 · Pick a unit <ChevronRight className="h-3 w-3" /> {grade}
                </p>
                {unitsLoading ? (
                  <div className="ae-card p-8 flex items-center justify-center gap-3 text-[#4a6984]">
                    <Loader2 className="h-5 w-5 animate-spin" /> Loading units…
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                    {contentUnits.map((u) => (
                      <UnitCard
                        key={u.unit_id}
                        unit={u}
                        selected={unit?.unit_id === u.unit_id}
                        onSelect={handleSelectUnit}
                      />
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Step 3 — mini-unit picker (single grade, when subdivided) */}
            {unit && !unit.is_pair && (unit.mini_units || []).length > 0 && (
              <div>
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/70 mb-2 flex items-center gap-2">
                  Step 3 · Pick the mini-unit <ChevronRight className="h-3 w-3" />{" "}
                  {unit.grade} · Unit {unit.unit_number}
                </p>
                <p className="text-xs text-[#4a6984]/70 mb-3 max-w-2xl leading-relaxed">
                  Unit {unit.unit_number} ({unit.day_budget}) is built around{" "}
                  {unit.mini_units.length} mini-units. Pick the one you're
                  starting — we'll scope the activation and diagnostic to its
                  expectations only.
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
                  {unit.mini_units.map((m) => (
                    <MiniUnitCard
                      key={m.label}
                      unit={unit}
                      mini={m}
                      selected={mini?.label === m.label}
                      onSelect={setMini}
                    />
                  ))}
                </div>
              </div>
            )}

            {/* Step 3 — split-grade mini-unit surfacing (pair labels only).
                Informational: two labelled descriptions + a tiered notice
                (amber Tier 1 / steel Tier 2 / mint positive), then how each
                mini-unit pairs across the two grades. */}
            {unit && unit.is_pair && (
              <div data-testid="math-engine-pair-surface">
                <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/70 mb-2 flex items-center gap-2">
                  Step 3 · How this unit divides <ChevronRight className="h-3 w-3" />{" "}
                  {unit.grade} · Unit {unit.unit_number}
                </p>

                <SplitNoticeBanner notice={unit.notice} />

                {(unit.mini_unit_pairs || []).length > 0 ? (
                  <>
                    {!unit.notice ? (
                      <p className="text-xs text-[#4a6984]/70 mb-3 max-w-2xl leading-relaxed">
                        Every mini-unit in Unit {unit.unit_number} pairs cleanly
                        across {unit.grade}. No divided stretches to plan around.
                      </p>
                    ) : null}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      {unit.mini_unit_pairs.map((p, i) => (
                        <MiniUnitPairCard
                          key={`${p.lower?.label || ""}-${p.upper?.label || ""}-${i}`}
                          pair={p}
                          unitNumber={unit.unit_number}
                          noticeTier={unit.notice?.tier}
                        />
                      ))}
                    </div>
                  </>
                ) : (
                  !unit.notice && (
                    <p
                      data-testid="math-engine-pair-no-minis"
                      className="text-sm text-[#4a6984]/80 max-w-2xl leading-relaxed rounded-xl border-2 border-[#96ded1]/50 bg-[#96ded1]/10 px-4 py-3"
                    >
                      Unit {unit.unit_number} — {unit.name} runs together across{" "}
                      {unit.grade}. No mini-units, no split to plan around.
                    </p>
                  )
                )}

                <div
                  data-testid="math-engine-split-planning-panel"
                  className="mt-5 rounded-xl border-2 border-[#4a6984]/30 bg-[#4a6984]/10 p-5 md:p-6"
                >
                  <p className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#4a6984]/80 mb-2">
                    This is a planning view
                  </p>
                  <p className="text-sm text-[#1c2d3d]/90 leading-relaxed max-w-2xl">
                    Split-grade lesson generation is not available yet. What you
                    see here is the full picture of how this unit divides across
                    your two grades — which mini-units share content, which run
                    one grade at a time, and roughly how long each divided
                    stretch lasts.
                  </p>
                  <p className="mt-2 text-sm text-[#1c2d3d]/90 leading-relaxed max-w-2xl">
                    Use it to plan your unit. Single-grade lesson generation is
                    available from the grade buttons on the first screen.
                  </p>
                </div>
              </div>
            )}
            {unit &&
              !unit.is_pair &&
              ((unit.mini_units || []).length === 0 || mini) && (
                <ConfirmPanel
                  unit={unit}
                  mini={mini}
                  onProceed={runGeneration}
                  onBack={() => {
                    if (mini) setMini(null);
                    else setUnit(null);
                  }}
                />
              )}
          </div>
        )}
      </section>

      {unitTestDialogOpen && unit && (
        <UnitTestGeneratorDialog
          open={unitTestDialogOpen}
          onClose={() => setUnitTestDialogOpen(false)}
          grade={unit.grade}
          unitNumber={unit.unit_number}
          unitName={unit.name}
          miniUnitLabel={mini?.label || ""}
          onGenerated={() => {
            // The two new activities show up in the Library; the
            // unit-summary screen stays open so the teacher can keep
            // navigating from there.
          }}
        />
      )}
    </main>
  );
};

export default MathEnginePage;
